import { Alert, Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import type { VirtualAccountRoleView } from '@/session/useVirtualAccountRole';

/**
 * The grace notice (TMMAPP-0110, MM-D-116) on the virtual account and envelope pages while the owner
 * is in GRACE: "{owner}'s subscription ends {date}: this virtual account becomes read-only until they
 * renew." The date is the served `readOnlyUntil` (a civil date, rendered as received). Nothing for
 * ACTIVE or LOCKED.
 */
export function GraceNotice({ role, owner }: { role: VirtualAccountRoleView; owner: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (role.ownerAccess !== 'GRACE') return null;
  const until = role.readOnlyUntil;
  const key = role.isOwner
    ? until !== null
      ? 'roles.graceOwner'
      : 'roles.graceOwnerNoDate'
    : until !== null
      ? 'roles.grace'
      : 'roles.graceNoDate';
  return (
    <Alert severity="warning" data-grace-notice="">
      {t(key, { owner, date: until !== null ? formatCivilDate(until, locale) : '' })}
    </Alert>
  );
}

/**
 * The locked state of one virtual account (TMMAPP-0110, MM-D-116): its owner is locked, so a member
 * sees nothing inside it until they renew. The page keeps its header; this replaces the content.
 */
export function VirtualAccountLockedBody({ owner }: { owner: string }) {
  const { t } = useTranslation();
  return (
    <Box component="section" data-virtual-account-locked="">
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t('roles.lockedBody', { owner })}
      </Typography>
      <Button variant="outlined" component={RouterLink} to="/me/virtual-accounts">
        {t('roles.lockedBack')}
      </Button>
    </Box>
  );
}
